import Box from '@components/Box';
import PlacingOrderAddressDropdownItem
  from '../PlacingOrderAddressDropdownItem/PlacingOrderAddressDropdownItem';
import PlacingOrderAddressEnglish from '../PlacingOrderAddressEnglish';
import SelectContainer from '@containers/SelectContainer';
import SelectDropdown from '@components/SelectDropdown';
import TextInputContainer from '@containers/TextInputContainer';
import TextInputWithSuggestionsContainer from '@containers/TextInputWithSuggestionsContainer';
import { AddressTemplate, TLabelSelectedItem } from '@api/apiClient';
import { Column, Row } from '@components/Grid';
import {
  countries,
  countryKeyExtractor,
  CountrySelectOption,
  CountrySelectValue,
  CountryType
} from '@components/CountrySelect';
import { TCitySuggestion } from '@api/apiClient/address';
import { IPlacingOrderAddressFieldsCNProps } from '../../types';
import { TRegion, TAddress } from '@api/apiClient/address';
import { useTranslation } from 'react-i18next';

const PlacingOrderAddressFieldsCN: React.FC<IPlacingOrderAddressFieldsCNProps> = (props: IPlacingOrderAddressFieldsCNProps): React.ReactElement => {
  const {
    availableCountries,
    control,
    country,
    emptyAddressWithUpdate,
    fullListAddresses,
    handlerGetFullAddress,
    handlerOnClickDeleteAddress,
    handlerSetCurrentAddress,
    handlerSetSelectedSuggestion,
    labelSelectedItem,
    region,
    regions,
    useSuggestions,
    useAddressSuggestionsForCity,
    useAddressSuggestionsForCityDistrict,
    withFullAddressInEnglish,
    isMunicipality,
  } = props;

  const { t } = useTranslation();

  const allowedIds = availableCountries ?? ['CN', 'HK'];

  const getAddrString = (value: TAddress) => `${value.address} ${value.title} ${value.adcode}`;

  const getSuggestionName = (value: TCitySuggestion) => value.nameCn

  return (
      <>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              <SelectDropdown<AddressTemplate>
                  defaultSelectedItem={emptyAddressWithUpdate}
                  items={fullListAddresses}
                  keyExtractor={(item: TLabelSelectedItem) => item.id}
                  label={t('placingOrderModalWindow.form.address.addressTitle')}
                  labelSelectedItem={() => handlerGetFullAddress(labelSelectedItem)}
                  onItemSelect={(id: string) => handlerSetCurrentAddress(id)}
                  renderItem={(props: { item: TLabelSelectedItem }) => (
                      <PlacingOrderAddressDropdownItem
                          handlerGetFullAddress={handlerGetFullAddress}
                          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
                          {...props}
                      />
                  )}
              />
            </Box>
          </Column>
        </Row>
        <Row>
          <Column phone={8}>
            <Box
                mb={5}
                pr={4}
            >
              <SelectContainer<CountryType, AddressTemplate>
                  control={control}
                  disabled={country !== 'unknown'}
                  items={countries.filter((item: CountryType) => allowedIds.includes(item.id))}
                  keyExtractor={countryKeyExtractor}
                  name="address.countryCode"
                  renderItem={CountrySelectOption}
                  renderSelectedItem={CountrySelectValue}
                  title={t('placingOrderModalWindow.form.address.country')}
              />
            </Box>
          </Column>
        </Row>
        <Row>
          <Column phone={4}>
            <Box mb={5} pr={4}>
              <SelectContainer<TRegion, AddressTemplate>
                  control={control}
                  items={regions}
                  keyExtractor={(province: TRegion | null) => province?.nameCn ?? ''}
                  labelOptionItem={(province: TRegion | null) => province?.nameCn ?? ''}
                  name="address.region"
                  title={t('placingOrderModalWindow.form.address.region')}
              />
            </Box>
          </Column>
          <Column phone={4}>
            <Box mb={5} pr={4}>
              <TextInputWithSuggestionsContainer<TCitySuggestion, AddressTemplate>
                  control={control}
                  disabled={!region || isMunicipality}
                  name="address.city"
                  title={t('placingOrderModalWindow.form.address.city')}
                  useSuggestions={useSuggestions}
                  getName={getSuggestionName}
                  getSelectionValue={getSuggestionName}
              />
            </Box>
          </Column>
          <Column phone={4}>
            <Box mb={5}>
              <TextInputWithSuggestionsContainer<TCitySuggestion, AddressTemplate>
                  control={control}
                  disabled={!region}
                  name="address.cityDistrict"
                  title={t('placingOrderModalWindow.form.address.cityDistrict')}
                  useSuggestions={useAddressSuggestionsForCity}
                  getName={getSuggestionName}
                  getSelectionValue={getSuggestionName}
              />
            </Box>
          </Column>
        </Row>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              <TextInputWithSuggestionsContainer<TAddress, AddressTemplate>
                  control={control}
                  disabled={!region}
                  name="address.street"
                  title={t('placingOrderModalWindow.form.address.street')}
                  useSuggestions={useAddressSuggestionsForCityDistrict}
                  onSelect={handlerSetSelectedSuggestion}
                  getName={getAddrString}
                  getSelectionValue={(value: TAddress) => value.address}
              />
            </Box>
          </Column>
        </Row>
        <Row>
          <Column phone={4}>
            <Box
                mb={8}
                pr={4}
            >
              <TextInputContainer
                  control={control}
                  name="address.house"
                  title={t('placingOrderModalWindow.form.address.house')}
              />
            </Box>
          </Column>
          <Column phone={4}>
            <Box
                mb={8}
                pr={4}
            >
              <TextInputContainer
                  control={control}
                  name="address.flat"
                  title={t('placingOrderModalWindow.form.address.flat')}
              />
            </Box>
          </Column>
          <Column phone={4}>
            <Box mb={8}>
              <TextInputContainer
                  control={control}
                  name="address.zipCode"
                  title={t('placingOrderModalWindow.form.address.zipCode')}
              />
            </Box>
          </Column>
        </Row>
        {withFullAddressInEnglish && (
            <PlacingOrderAddressEnglish control={control}/>
        )}
      </>
  );
};

export default PlacingOrderAddressFieldsCN;
